import { OrderStatusGroup } from '@tcr-erp/shared';
import type { TenantOrder } from '@/mocks/orders';
import type { B2BCreditLimit } from '@/services/b2b-receivable.service';
import { formatVND } from '@/utils/format';
import { buildConfirmOrderPrompt, isGtPrepaidOrder } from '../components/confirmOrderPrompt';
import { FULFILLMENT_ACTION_ROLES } from './orderActionRoles';
import { partitionOrdersForBulkCancel } from './bulkCancelOrders';
import { canCancelOrder } from './orderHeaderActions';

export interface ExcludedOrder { order: TenantOrder; reason: string }
export interface BatchActionPlan { eligible: TenantOrder[]; excluded: ExcludedOrder[] }
/**
 * TCR-2504 — mỗi nút chuyển trạng thái có kế hoạch riêng, khác `null` khi CÓ ÍT NHẤT 1 đơn đang ở
 * trạng thái đó (kể cả khi chọn lẫn trạng thái): chọn 50 đơn có 13 đơn Chờ xác nhận ⇒ "Xác nhận (13)",
 * bấm chỉ xử lý 13 đơn đó. Trước đây chỉ có khi MỌI đơn cùng trạng thái.
 */
export interface BatchOrderPlans {
  submitDraft: BatchActionPlan | null;
  confirm: BatchActionPlan | null;
  advancePacking: BatchActionPlan | null;
  cancel: BatchActionPlan;
  tags: BatchActionPlan | null;
  skip: BatchActionPlan;
  /**
   * TCR-2439 v3 — khách có các đơn được đưa vào "Xác nhận (N)" mà cộng lại VƯỢT hạn mức hiện tại
   * (chỉ người có quyền vượt hạn mức mới có mục này) — hộp thoại hàng loạt nêu số liệu như duyệt từng đơn.
   */
  creditOverLimit: CustomerCreditCheck[];
}
export interface BatchPlanContext {
  roles: readonly string[];
  canOverrideLock: boolean;
  canConfirmGtPrepaid: boolean;
  canApproveCredit: boolean;
  canAssignTags: boolean;
  /**
   * TCR-2439 — hạn mức HIỆN TẠI theo `user_id` của khách tổ chức có đơn MT/Sỉ đang chọn
   * (`creditCheckCustomerIds`). Thiếu khách (chưa tải xong, hoặc không có quyền xem công nợ) ⇒
   * giữ luật cũ theo cờ lưu trên đơn.
   */
  creditLimits?: Record<string, CurrentCreditLimit>;
}

export type CurrentCreditLimit = Pick<B2BCreditLimit, 'credit_limit' | 'current_debt' | 'is_unlimited'>;

export interface CustomerCreditCheck {
  customerId: string;
  /** Các đơn đang chọn (không phải GT) của khách, theo thứ tự chọn. */
  orders: TenantOrder[];
  creditLimit: number;
  currentDebt: number;
  /** Tổng tiền các đơn đang chọn của khách. */
  selectedTotal: number;
  /** Công nợ dự kiến nếu duyệt hết các đơn đang chọn. */
  debtAfter: number;
  /** Số tiền vượt hạn mức; 0 = còn trong hạn mức. */
  excess: number;
}

const RETURNED_FOR_DISCOUNT = 'Đơn đã bị trả lại để sửa chiết khấu';
const NEEDS_SINGLE_REVIEW = 'Cần duyệt riêng từng đơn';
const NEEDS_CREDIT_APPROVE = 'Bạn cần quyền "Duyệt đơn hàng vượt hạn mức nợ"';
const LOCKED = 'Đơn đã khoá theo trạng thái';
const NOT_CANCELABLE = 'Đơn đang giao hoặc đã kết thúc, không huỷ được';
const NO_FULFILLMENT_ROLE = 'Bạn không có quyền chuyển đơn sang Đang đóng gói';
const ALREADY_SKIPPED = 'Đơn đã được đánh dấu bỏ qua';
const NOT_DRAFT = 'Không phải đơn Nháp';
const NOT_AWAITING_CONFIRMATION = 'Không ở trạng thái Chờ xác nhận';
const NOT_AWAITING_PACKING = 'Không ở trạng thái Chờ đóng gói';
export const TERMINAL = 'Đơn đã huỷ hoặc đã hoàn';

export function split(rows: TenantOrder[], reasonOf: (order: TenantOrder) => string | null): BatchActionPlan {
  const plan: BatchActionPlan = { eligible: [], excluded: [] };
  for (const order of rows) {
    const reason = reasonOf(order);
    if (reason) plan.excluded.push({ order, reason });
    else plan.eligible.push(order);
  }
  return plan;
}

/** Lý do chung khi KHÔNG đơn nào đủ điều kiện — hiện ở tooltip nút mờ của panel. */
export function excludedReasons(plan: BatchActionPlan): string | undefined {
  if (plan.eligible.length > 0 || plan.excluded.length === 0) return undefined;
  return Array.from(new Set(plan.excluded.map((item) => item.reason))).join(' · ');
}

export function isTerminalOrder(order: TenantOrder): boolean {
  return order.status_group === OrderStatusGroup.CANCELLED || order.status_group === OrderStatusGroup.RETURNED;
}

/**
 * Kế hoạch cho một nút chuyển trạng thái: `null` (ẩn nút) khi không đơn nào ở `group`; đơn khác
 * trạng thái bị loại với `otherGroupReason`, đơn đúng trạng thái xét tiếp bằng `reasonOf`.
 */
export function planForStatusGroup(
  rows: TenantOrder[],
  group: OrderStatusGroup,
  otherGroupReason: string,
  reasonOf: (order: TenantOrder) => string | null = () => null,
): BatchActionPlan | null {
  if (!rows.some((order) => order.status_group === group)) return null;
  return split(rows, (order) => (order.status_group === group ? reasonOf(order) : otherGroupReason));
}

export function commonStatusGroup(rows: TenantOrder[]): OrderStatusGroup | null {
  const groups = new Set(rows.map((row) => row.status_group ?? null));
  if (groups.size !== 1) return null;
  const [group] = Array.from(groups);
  return (group as OrderStatusGroup | null) ?? null;
}

/**
 * TCR-2439 v3 — đơn cần tính lại theo hạn mức HIỆN TẠI lúc duyệt: đơn khách tổ chức, không phải GT
 * (GT trả trước, không xét hạn mức), chưa có quyết định duyệt/từ chối vượt hạn mức. Cờ `pending` lẫn
 * `not_required` gắn lúc tạo đơn đều có thể lỗi thời khi hạn mức được nâng/hạ sau đó. Khớp BE
 * `confirmOrder()` (`orders-aggregate.service.ts`).
 */
export function needsCurrentCreditCheck(order: TenantOrder): boolean {
  return order.user_type === 'organization'
    && !isGtPrepaidOrder(order)
    && Boolean(order.user_id)
    && order.credit_approval_status !== 'approved'
    && order.credit_approval_status !== 'rejected';
}

/** Khách cần tải hạn mức hiện tại cho các đơn đang chọn. */
export function creditCheckCustomerIds(rows: TenantOrder[]): string[] {
  return Array.from(new Set(rows.filter(needsCurrentCreditCheck).map((order) => String(order.user_id))));
}

/**
 * TCR-2439 — hạn mức hiện tại của từng khách nếu duyệt hết các đơn đang chọn (`current_debt` + tổng
 * các đơn cần xét của khách đó). Chỉ tính cho khách đã có số liệu hạn mức; tắt kiểm tra ⇒ excess 0.
 */
function creditCheckByCustomer(rows: TenantOrder[], limits: Record<string, CurrentCreditLimit>): Map<string, CustomerCreditCheck> {
  const checks = new Map<string, CustomerCreditCheck>();
  for (const customerId of creditCheckCustomerIds(rows)) {
    const limit = limits[customerId];
    if (!limit) continue;
    const orders = rows.filter((order) => needsCurrentCreditCheck(order) && String(order.user_id) === customerId);
    const selectedTotal = orders.reduce((sum, order) => sum + (order.summary?.total ?? order.total_amount ?? 0), 0);
    const currentDebt = limit.current_debt ?? 0;
    const creditLimit = limit.credit_limit ?? 0;
    const debtAfter = currentDebt + selectedTotal;
    checks.set(customerId, {
      customerId,
      orders,
      creditLimit,
      currentDebt,
      selectedTotal,
      debtAfter,
      excess: limit.is_unlimited ? 0 : Math.max(0, debtAfter - creditLimit),
    });
  }
  return checks;
}

/** TCR-2264 — cùng luật panel khi chọn 1 đơn (`buildSingleOrderSections`), áp cho nhiều đơn. */
export function planBatchOrderActions(rows: TenantOrder[], ctx: BatchPlanContext): BatchOrderPlans {
  const canFulfill = FULFILLMENT_ACTION_ROLES.some((role) => ctx.roles.includes(role));

  // TCR-2372 — khác các nút chuyển trạng thái khác: chọn lẫn trạng thái vẫn gửi được phần đơn Nháp, đơn còn lại bỏ qua.
  const submitDraft = planForStatusGroup(rows, OrderStatusGroup.DRAFT, NOT_DRAFT);

  // Hạn mức chỉ cộng các đơn sẽ được duyệt (Chờ xác nhận) — đơn khác trạng thái trong vùng chọn không tính.
  const awaitingConfirmation = rows.filter((order) => order.status_group === OrderStatusGroup.AWAITING_CONFIRMATION);
  const creditChecks = creditCheckByCustomer(awaitingConfirmation, ctx.creditLimits ?? {});
  const confirm = planForStatusGroup(rows, OrderStatusGroup.AWAITING_CONFIRMATION, NOT_AWAITING_CONFIRMATION, (order) => {
    if (order.discount_review?.returned) return RETURNED_FOR_DISCOUNT;
    const check = needsCurrentCreditCheck(order) ? creditChecks.get(String(order.user_id)) : undefined;
    // TCR-2439 — cờ gắn lúc tạo đơn có thể lỗi thời (hạn mức nâng/hạ sau đó): có số liệu hiện tại thì
    // xét theo tổng các đơn đang chọn của khách. Trong hạn mức ⇒ duyệt như đơn thường; vượt ⇒ chỉ người
    // có quyền vượt hạn mức, và hộp thoại nêu số liệu (`creditOverLimit`). BE cũng tính lại khi duyệt.
    if (check) {
      if (check.excess === 0 || ctx.canApproveCredit) return null;
      return `Tổng các đơn đang chọn của khách vượt hạn mức công nợ ${formatVND(check.excess)}. ${NEEDS_CREDIT_APPROVE}`;
    }
    const prompt = buildConfirmOrderPrompt({
      creditApprovalStatus: order.credit_approval_status,
      creditApprovalReason: order.credit_approval_reason,
      creditWarning: null,
      canApproveCredit: ctx.canApproveCredit,
      isGtPrepaid: isGtPrepaidOrder(order),
      canConfirmGtPrepaid: ctx.canConfirmGtPrepaid,
      orderTotal: order.total_amount,
    });
    if (prompt.kind === 'blocked') return prompt.content;
    // GT trả trước / vượt hạn mức: luồng từng đơn hiện cảnh báo riêng (kiểm tiền đã về, hạn mức) mà
    // hộp thoại hàng loạt không thay được — để người dùng duyệt riêng từng đơn.
    if (prompt.kind === 'gt-prepaid' || prompt.kind === 'over-limit') return `${NEEDS_SINGLE_REVIEW}: ${prompt.title}`;
    return null;
  });

  const advancePacking = planForStatusGroup(rows, OrderStatusGroup.AWAITING_PACKING, NOT_AWAITING_PACKING,
    () => (canFulfill ? null : NO_FULFILLMENT_ROLE));

  const { cancelableIds } = partitionOrdersForBulkCancel(rows, ctx.canOverrideLock);
  const cancelable = new Set(cancelableIds);
  // TCR-2504 — cùng luật nút Huỷ từng đơn (`canCancelOrder`): đơn Đang giao/Thành công/Hoàn/Huỷ không
  // huỷ được kể cả với quyền vượt khoá — trước đây vẫn được đếm vào "Huỷ (n)" rồi BE từ chối từng đơn.
  const cancel = split(rows, (order) => {
    if (!canCancelOrder(order.state, order.status_group)) return NOT_CANCELABLE;
    return cancelable.has(order._id) ? null : LOCKED;
  });

  const tags = ctx.canAssignTags ? split(rows, () => null) : null;

  const skip = split(rows, (order) => {
    if (isTerminalOrder(order)) return TERMINAL;
    return order.skipped ? ALREADY_SKIPPED : null;
  });

  const eligibleIds = new Set(confirm?.eligible.map((order) => order._id) ?? []);
  const creditOverLimit = Array.from(creditChecks.values())
    .filter((check) => check.excess > 0 && check.orders.some((order) => eligibleIds.has(order._id)));

  return { submitDraft, confirm, advancePacking, cancel, tags, skip, creditOverLimit };
}
